import React from 'react';
import { useTranslations } from 'next-intl';
import { Input } from '@/shared/ui/Input';
import { Label } from '@/shared/ui/Label';
import { NoCrawlingSite } from '@/shared/lib/exceptions.constant';
import DocumentUploadSection from './DocumentUploadSection';
import { AgentConstructionItem } from '../model/agent.types';
import TrashIcon from '@/shared/ui/icon/TrashIcon';

export default function AgentDataUploader({
  index,
  item,
  updateItem,
  removeItem,
  onErrorChange,
  disabled,
  showToast,
}: {
  index: number;
  item: AgentConstructionItem;
  updateItem: (
    index: number,
    updater: (prevItem: AgentConstructionItem) => AgentConstructionItem
  ) => void;
  removeItem: () => void;
  onErrorChange?: (index: number, error: string | null) => void;
  disabled: boolean;
  showToast?: (message: string) => void;
}) {
  const t = useTranslations('Demo');
  const [errorMsg, setErrorMsg] = React.useState<string | null>(null);
  return (
    <div
      className={
        'flex flex-col justify-between gap-[28px] rounded-md border border-gray-100 bg-white p-[20px]'
      }
    >
      <div className="flex w-full grow flex-col gap-[28px] text-gray-800">
        <div className="flex justify-between font-light">
          <span className={'text-title1 text-primary-dark font-[600]'}>
            {t('데이터')} {index + 1}
          </span>

          {index > 0 && (
            <button
              className={'text-gray-300'}
              onClick={removeItem}
              aria-label="데이터 삭제"
            >
              <TrashIcon />
            </button>
          )}
        </div>
        <div className={'flex flex-col gap-[12px]'}>
          <Label htmlFor="item-name" className={'front-[400] text-label1'}>
            {t('데이터 이름')}
            <span className={'text-caption2 text-danger ml-[4px]'}>
              {t('필수')}
            </span>
          </Label>
          <Input
            disabled={disabled}
            name={'item-name'}
            id={`item-title-${index}`}
            className={
              'h-[60px] rounded-md border border-gray-100 bg-blue-50 px-[20px] placeholder:text-gray-300'
            }
            placeholder={t('데이터 이름을 입력해주세요')}
            value={item.title}
            onChange={(e) => {
              updateItem(index, (prevItem) => ({
                ...prevItem,
                title: e.target.value,
              }));
            }}
          />
        </div>
        <div className={'flex flex-col gap-[12px]'}>
          <Label htmlFor="item-url" className={'front-[400] text-label1'}>
            {t('데이터 URL 주소')}
          </Label>
          <div className={'relative'}>
            <Input
              disabled={disabled}
              type="url"
              name="item-url"
              id={`item-url-${index}`}
              className={
                'h-[60px] rounded-md border border-gray-100 bg-blue-50 px-[20px] placeholder:text-gray-300'
              }
              placeholder={t('학습할 URL을 입력해 주세요')}
              value={item.url}
              onChange={(e) => {
                const notValidUrl = NoCrawlingSite.some((noCrawling) => {
                  if (e.target.value.includes(noCrawling)) {
                    return true;
                  }
                  return false;
                });
                const errorMessage = notValidUrl
                  ? t('이 사이트는 자동 수집이 지원되지 않습니다')
                  : null;
                setErrorMsg(errorMessage);
                onErrorChange?.(index, errorMessage);
                updateItem(index, (prevItem) => ({
                  ...prevItem,
                  url: e.target.value.trim(),
                }));
              }}
            />
            <div
              className={
                'text-caption1 text-danger absolute bottom-[-20px] left-[8px] w-full font-[400]'
              }
            >
              {errorMsg}
            </div>
          </div>
        </div>
        <DocumentUploadSection
          disabled={disabled}
          index={index}
          documents={item.documents}
          showToast={showToast}
          onDocumentsChange={(documents) => {
            updateItem(index, (prevItem) => ({
              ...prevItem,
              documents: documents,
            }));
          }}
        />
      </div>
    </div>
  );
}
